/**
 * VoxResponse detail component
 */
import { map } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs'

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';

import { ProfileService } from '../services/profile.service';
import { VoxPopService } from '../services/voxpop.service';
import { VoxResponse } from '../types/response';
import { AudioAPIWrapper } from './audio.wrapper';

enum State {
  Stopped = 1,
  Playing,
  Paused,
}


@Component({
  selector: 'response-player',
  templateUrl: './response-player.component.html',
  styleUrls: ['./response-player.component.css'],
  providers: [VoxPopService],
  standalone: false
})

export class VoxResponsePlayerComponent implements OnInit {
  @Input() response: VoxResponse;
  @Input() observableResponse: Observable<VoxResponse>;
  @Input() publishUpdating: boolean = false;
  @Input() publishActionLabel: string = 'Publish';
  @Input() publishActionTitle: string = 'Publish response';
  @Input() publishMessage: string | null = null;
  @Input() publishMessageIsError: boolean = false;
  @Output() cancelPlayer: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() responseChange: EventEmitter<VoxResponse> = new EventEmitter<VoxResponse>();
  @Output() publishToggle: EventEmitter<VoxResponse> = new EventEmitter<VoxResponse>();
  errorMessage: string;
  private audio: AudioAPIWrapper;
  audioURL: string;
  transcriptDraft: string = '';
  private savedTranscript: string = '';
  isSavingTranscript: boolean = false;
  transcriptSaveError: string | null = null;
  transcriptSaveMessage: string | null = null;
  progressbarWidth: number = 0;
  duration: number;
  position: number;
  currentState: State = State.Stopped;
  positionObserver: Observable<number>;
  private positionSubscription: Subscription | null = null;
  lastPosition: number = 0;
  audioLoading: boolean = false;
  audioReady: boolean = false;
  audioError: string | null = null;
  audioStatusMessage: string | null = null;

  constructor(
    private voxPopService: VoxPopService,
  ) { }

  ngOnInit(): void {
    if (!ProfileService.IsLoggedIn()) return;

    try {
      this.audio = new AudioAPIWrapper();
      this.bindAudioEvents();
    } catch (e) {
      this.setAudioError('Audio is unavailable for this response. Try downloading the file.');
    }

    this.observableResponse.subscribe(
      (r) => this.setVoxResponse(r),
    );
    console.log("So we should be subscribed now...");

    this.setVoxResponse(this.response);
  }

  Play(): void {
    if (!this.audio || !this.audioReady || this.audioLoading || this.audioError) return;
    if (this.currentState != State.Playing) try {
      this.audioError = null;
      this.audioLoading = true;
      this.audioStatusMessage = 'Starting audio...';
      this.currentState = State.Playing;
      this.audio.Play().then(
        () => {
          if (this.currentState != State.Playing) return;
          this.audioLoading = false;
          this.audioStatusMessage = 'Audio playing.';
          this.startProgress();
        },
        () => this.setAudioError('Audio playback failed. Try downloading the file.'),
      );
    } catch (e) {
      this.setAudioError('Audio playback failed. Try downloading the file.');
    }
  }

  isPlaying(): boolean {
    return this.currentState == State.Playing;
  }

  Pause(): void {
    if (this.currentState == State.Playing) try {
      this.audio.Pause();
      this.currentState = State.Paused;
      this.audioLoading = false;
      this.audioStatusMessage = 'Audio paused.';
      this.stopProgress();
    } catch (e) {
      this.setAudioError('Audio playback failed. Try downloading the file.');
    }
  }

  Stop(): void {
    if (!this.audio || this.currentState == State.Stopped) return;
    try {
      this.audio.Stop();
      this.audio.CurrentTime(0);
    } catch (e) {
      this.setAudioError('Audio playback failed. Try downloading the file.');
      return;
    }
    this.currentState = State.Stopped;
    this.positionObserver = null;
    this.stopProgress();
    this.lastPosition = 0;
    this.progressbarWidth = 0;
    this.audioLoading = false;
    this.audioStatusMessage = 'Audio stopped.';
  }

  Cancel() {
    this.transcriptDraft = this.savedTranscript;
    this.resetTranscriptSaveState();
    this.cancelPlayer.emit(true);
  }

  startProgress(): void {
    this.stopProgress();
    let currentPosition: number;
    this.positionObserver = interval(50).pipe(
      map(() => {
        try {
          currentPosition = Math.round(this.audio.CurrentTime(null) * 100) / 100;
          if (!this.duration || isNaN(this.duration)) {
            this.duration = Math.round(this.audio.GetDuration() * 100) / 100;
          }
          if (this.lastPosition != currentPosition) {
            this.lastPosition = currentPosition;
            this.progressbarWidth = this.duration > 0
              ? (this.lastPosition / this.duration) * 100
              : 0;
            // console.log("Pos: " + currentPosition + ", percentage: " + percentage + ", barWidth: " + this.progressbarWidth);
          }
        } catch (e) {
          console.log(e.message);
        }
        if (this.lastPosition >= this.duration) {
          this.Stop();
        }
        return this.lastPosition / this.duration;
      }));
    this.positionSubscription = this.positionObserver.subscribe();
  }

  public progressBarPercent(): number {
    if (!isFinite(this.progressbarWidth)) return 0;
    return Math.max(0, Math.min(100, this.progressbarWidth));
  }

  get canPlayAudio(): boolean {
    return !!this.response && this.audioReady && !this.audioLoading && !this.audioError;
  }

  get canStopAudio(): boolean {
    return !!this.audio && this.currentState != State.Stopped && !this.audioError;
  }

  retryAudio(): void {
    if (!this.response || this.audioLoading) return;
    this.loadAudio();
  }

  requestPublishToggle(): void {
    if (!this.response || this.publishUpdating) return;
    this.publishToggle.emit(this.response);
  }

  get senderLabel(): string {
    return this.cleanText(this.response && this.response.name) || 'Unknown sender';
  }

  get locationLabel(): string {
    return this.cleanText(this.response && this.response.location) || 'No location';
  }

  get topicLabel(): string {
    return this.cleanText(this.response && this.response.topic_short_name) || 'Topic unavailable';
  }

  get receivedValue(): string | null {
    const received = this.cleanText(this.response && this.response.received);
    return received && !isNaN(Date.parse(received)) ? received : null;
  }

  get hasDuration(): boolean {
    return !!this.response && typeof this.response.duration === 'number' &&
      isFinite(this.response.duration) && this.response.duration >= 0;
  }

  get contactEmail(): string | null {
    return this.cleanText(this.response && this.response.email);
  }

  get contactPhone(): string | null {
    return this.cleanText(this.response && this.response.phone);
  }

  get publishStatusLabel(): string {
    return this.response && this.response.published ? 'Published' : 'Draft';
  }

  get transcriptStatusLabel(): string {
    if (!this.response) return 'No transcript';
    if (this.response.corrected_transcript) return 'Edited transcript';
    return this.hasTranscript ? 'Auto transcript' : 'No transcript';
  }

  get hasTranscript(): boolean {
    return !!this.response && (
      !!this.response.corrected_transcript ||
      (!!this.response.transcript && this.response.transcript !== '[No speech detected]')
    );
  }

  get audioStatus(): 'available' | 'absent' | 'unknown' {
    if (this.response && typeof this.response.mimetype === 'string' && this.response.mimetype.trim()) {
      return 'available';
    }
    if (this.response && Object.prototype.hasOwnProperty.call(this.response, 'mimetype') &&
        (this.response.mimetype === null || this.response.mimetype === '')) {
      return 'absent';
    }
    return 'unknown';
  }

  get audioStatusLabel(): string {
    switch (this.audioStatus) {
      case 'available':
        return 'Audio available';
      case 'absent':
        return 'No audio';
      default:
        return 'Audio status unknown';
    }
  }

  setVoxResponse(response: VoxResponse) {
    if (!response) {
      this.response = null;
      this.transcriptDraft = '';
      this.savedTranscript = '';
      this.resetTranscriptSaveState();
      this.resetAudioState();
      return;
    }
    console.log("Loaded response " + response.name + "(" + response.id + ")");
    this.response = response;
    this.savedTranscript = this.getSavedTranscript(response);
    this.transcriptDraft = this.savedTranscript;
    this.resetTranscriptSaveState();
    this.audioURL = `/api/private/r/audio/${this.response.id}/opus/${this.response.topic}-${this.response.id}-${this.response.name}.opus`;
    this.loadAudio();
  }

  get isTranscriptDirty(): boolean {
    return !!this.response && this.transcriptDraft !== this.savedTranscript;
  }

  onTranscriptDraftChange(transcript: string): void {
    this.transcriptDraft = transcript;
    this.transcriptSaveError = null;
    this.transcriptSaveMessage = null;
  }

  private loadAudio(): void {
    if (!this.audio || !this.audioURL) {
      this.setAudioError('Audio is unavailable for this response. Try downloading the file.');
      return;
    }
    this.stopProgress();
    this.currentState = State.Stopped;
    this.audioLoading = true;
    this.audioReady = false;
    this.audioError = null;
    this.audioStatusMessage = 'Loading audio...';
    try {
      this.duration = this.audio.Load(this.audioURL);
      this.progressbarWidth = 0;
      this.lastPosition = 0;
      if (isNaN(this.duration)) {
        this.duration = this.response.duration;
      }
    } catch (e) {
      this.setAudioError('Audio could not be loaded. Try downloading the file.');
    }
  }

  private bindAudioEvents(): void {
    this.audio.On('loadstart', () => {
      this.audioLoading = true;
      this.audioReady = false;
      this.audioError = null;
      this.audioStatusMessage = 'Loading audio...';
    });
    this.audio.On('loadedmetadata', () => {
      const mediaDuration = this.audio.GetDuration();
      if (isFinite(mediaDuration) && mediaDuration > 0) this.duration = mediaDuration;
    });
    this.audio.On('canplay', () => {
      this.audioLoading = false;
      this.audioReady = true;
      this.audioError = null;
      if (this.currentState == State.Playing) {
        this.audioStatusMessage = 'Audio playing.';
      } else if (this.currentState == State.Paused) {
        this.audioStatusMessage = 'Audio paused.';
      } else if (this.audioStatusMessage !== 'Audio stopped.') {
        this.audioStatusMessage = 'Audio ready.';
      }
    });
    this.audio.On('playing', () => {
      this.audioLoading = false;
      this.audioReady = true;
      this.audioStatusMessage = 'Audio playing.';
    });
    this.audio.On('waiting', () => {
      if (this.currentState == State.Playing) {
        this.audioLoading = true;
        this.audioStatusMessage = 'Loading audio...';
      }
    });
    this.audio.On('ended', () => {
      this.currentState = State.Stopped;
      this.audioLoading = false;
      this.audioStatusMessage = 'Audio finished.';
      this.stopProgress();
      this.progressbarWidth = 100;
    });
    this.audio.On('error', () => {
      this.setAudioError('Audio could not be loaded. Try downloading the file.');
    });
  }

  private setAudioError(message: string): void {
    this.stopProgress();
    this.currentState = State.Stopped;
    this.audioLoading = false;
    this.audioReady = false;
    this.audioStatusMessage = null;
    this.audioError = message;
  }

  private resetAudioState(): void {
    this.stopProgress();
    this.currentState = State.Stopped;
    this.audioLoading = false;
    this.audioReady = false;
    this.audioError = null;
    this.audioStatusMessage = null;
    this.lastPosition = 0;
    this.progressbarWidth = 0;
  }

  private stopProgress(): void {
    if (this.positionSubscription) {
      this.positionSubscription.unsubscribe();
      this.positionSubscription = null;
    }
  }

  private cleanText(value: string | null | undefined): string | null {
    if (typeof value !== 'string') return null;
    const cleaned = value.trim();
    return cleaned || null;
  }

  responseSaved(response: VoxResponse): void {
    console.log("Response was saved: " + response.id);
    this.response = response;
    this.savedTranscript = this.getSavedTranscript(response);
    this.transcriptDraft = this.savedTranscript;
    this.isSavingTranscript = false;
    this.transcriptSaveError = null;
    this.transcriptSaveMessage = 'Transcript saved.';
    this.responseChange.emit(response);
  }

  updateTranscript(): void {
    if (!this.response || !this.isTranscriptDirty || this.isSavingTranscript) return;
    const savedResponse: VoxResponse = {
      ...this.response,
      corrected_transcript: this.transcriptDraft,
    };
    this.isSavingTranscript = true;
    this.transcriptSaveError = null;
    this.transcriptSaveMessage = null;
    console.log("Saving response: " + savedResponse.id);
    this.voxPopService.updateResponse(savedResponse, true).subscribe(
      (response) => this.responseSaved(response),
      (error) => this.transcriptSaveFailed(error),
    )
  }

  private getSavedTranscript(response: VoxResponse): string {
    return response.corrected_transcript || response.transcript || '';
  }

  private transcriptSaveFailed(_error: any): void {
    this.isSavingTranscript = false;
    this.transcriptSaveMessage = null;
    this.transcriptSaveError = 'Unable to save transcript. Please try again.';
  }

  private resetTranscriptSaveState(): void {
    this.isSavingTranscript = false;
    this.transcriptSaveError = null;
    this.transcriptSaveMessage = null;
  }
}
